import React, { useEffect } from "react";
import { Line, Circle } from "react-konva";

export default function WallColumnBeam({
    position,
    lanterns,
    colorList,
    luminaires,
    props,
    rotateAround,
    barCenter,
    centerOffset,
    barThickness,
    lineRefs
}) {
    useEffect(() => {
        if (props.mountingType !== "wall") return;

        const columnAngle = props.positionAngle ?? 0;
        const minAngle = (columnAngle - 90 + 360) % 360;
        const maxAngle = (columnAngle + 90) % 360;

        const offsets = props.lanterns.map((l) => parseFloat(l.offset) || 0);
        const minOffset = Math.min(...offsets);
        const maxOffset = Math.max(...offsets);
        const centerOffset = (minOffset + maxOffset) / 2;
        const barThickness = 0.4;

        const barCenter = { x: position.x, y: position.y };

        const rotateAround = (point, center, angleRad) => {
            const translated = {
                x: point.x - center.x,
                y: point.y - center.y,
            };
            const rotated = {
                x: translated.x * Math.cos(angleRad) - translated.y * Math.sin(angleRad),
                y: translated.x * Math.sin(angleRad) + translated.y * Math.cos(angleRad),
            };
            return {
                x: center.x + rotated.x,
                y: center.y + rotated.y,
            };
        };

        const angleDiff = (a, b) => {
            let d = a - b;
            while (d > 180) d -= 360;
            while (d < -180) d += 360;
            return d;
        };

        const clampAngle = (angle, min, max) => {
            const diffToMin = Math.abs(angleDiff(angle, min));
            const diffToMax = Math.abs(angleDiff(angle, max));
            return diffToMin < diffToMax ? min : max;
        };

        const updatedLanterns = props.lanterns.map((lantern) => {
            const currentAngle = lantern.positionAngle ?? 0;

            const isAllowed =
                minAngle < maxAngle
                    ? currentAngle >= minAngle && currentAngle <= maxAngle
                    : currentAngle >= minAngle || currentAngle <= maxAngle;

            const offset = (parseFloat(lantern.offset) || 0) - centerOffset;
            const armX = offset;
            const armY = -barThickness / 2;

            const baseRot = rotateAround(
                { x: barCenter.x + armX, y: barCenter.y + armY },
                barCenter,
                (columnAngle * Math.PI) / 180
            );

            let positionAngle = currentAngle;
            let beamEndPoint = lantern.beamAimingInfo?.beamEndPoint;
            const beamLength = lantern.beamAimingInfo?.beamLength ?? 0.75;

            if (!isAllowed) {
                positionAngle = clampAngle(currentAngle, minAngle, maxAngle);
                const angleRad = ((positionAngle - 90) * Math.PI) / 180;

                const beamDir = {
                    x: Math.cos(angleRad),
                    y: Math.sin(angleRad),
                };

                const endX = baseRot.x + beamLength * beamDir.x;
                const endY = baseRot.y + beamLength * beamDir.y;

                const toEnd = {
                    x: endX - baseRot.x,
                    y: endY - baseRot.y,
                };

                const dot = toEnd.x * beamDir.x + toEnd.y * beamDir.y;

                beamEndPoint = dot >= 0
                    ? { x: endX, y: endY }
                    : { x: baseRot.x + 0.01 * beamDir.x, y: baseRot.y + 0.01 * beamDir.y };

            }


            return {
                ...lantern,
                positionAngle,
                beamAimingInfo: {
                    ...lantern.beamAimingInfo,
                    beamOrigin: baseRot,
                    beamEndPoint,
                },
            };
        });

        console.log("Updated lanterns:", updatedLanterns);
        props.updateLanterns(updatedLanterns, props.i);
    }, [props.positionAngle]);


    return lanterns.map((lantern, idx) => {
        if (!lineRefs.current[idx]) {
            lineRefs.current[idx] = React.createRef();
        }

        const lineRef = lineRefs.current[idx];
        const beamOrigin = lantern.beamAimingInfo?.beamOrigin;
        const beamEnd = lantern.beamAimingInfo?.beamEndPoint;

        if (!beamOrigin || !beamEnd) return null;

        const luminaireIndex = luminaires.indexOf(lantern.luminaireId);
        const resolvedColor =
            luminaireIndex !== -1 && colorList[luminaireIndex]
                ? colorList[luminaireIndex]
                : "lightgray";

        return (
            <React.Fragment key={idx}>
                <Line
                    ref={lineRef}
                    points={[beamOrigin.x, beamOrigin.y, beamEnd.x, beamEnd.y]}
                    stroke={resolvedColor === '#212529' ? "lightgray" : resolvedColor}
                    strokeWidth={3 / props.stageScale}
                />
                <Circle
                    x={beamEnd.x}
                    y={beamEnd.y}
                    radius={5 / props.stageScale}
                    fill={resolvedColor === '#212529' ? "lightgray" : resolvedColor}
                    draggable={props.selected}
                    onDragMove={(e) => {
                        const { x, y } = e.target.position();

                        const dx = x - beamOrigin.x;
                        const dy = y - beamOrigin.y;

                        let angle = Math.atan2(dy, dx) * (180 / Math.PI);
                        if (angle < 0) angle += 360;

                        const dragAngle = (angle + 90) % 360;

                        const columnAngle = props.positionAngle ?? 0;
                        const minAngle = (columnAngle - 90 + 360) % 360;
                        const maxAngle = (columnAngle + 90) % 360;

                        const angleDiff = (a, b) => {
                            let d = a - b;
                            while (d > 180) d -= 360;
                            while (d < -180) d += 360;
                            return d;
                        };

                        const clampAngle = (angle, min, max) => {
                            const diffToMin = Math.abs(angleDiff(angle, min));
                            const diffToMax = Math.abs(angleDiff(angle, max));
                            return diffToMin < diffToMax ? min : max;
                        };

                        const isAllowed = minAngle < maxAngle
                            ? dragAngle >= minAngle && dragAngle <= maxAngle
                            : dragAngle >= minAngle || dragAngle <= maxAngle;

                        const clampedAngle = isAllowed ? dragAngle : clampAngle(dragAngle, minAngle, maxAngle);

                        const beamLength = Math.sqrt(dx * dx + dy * dy);

                        const angleRad = ((clampedAngle - 90) * Math.PI) / 180;
                        const clampedX = beamOrigin.x + beamLength * Math.cos(angleRad);
                        const clampedY = beamOrigin.y + beamLength * Math.sin(angleRad);

                        e.target.position({ x: clampedX, y: clampedY });

                        if (lineRef.current) {
                            lineRef.current.points([beamOrigin.x, beamOrigin.y, clampedX, clampedY]);
                            lineRef.current.getLayer().batchDraw();
                        }

                        props.updateLanternBeamEndpoint({
                            beamEndPoint: { x: clampedX, y: clampedY },
                            beamLength,
                            positionAngle: clampedAngle,
                            lanternIndex: idx,
                            columnIndex: props.i,
                        });
                    }}


                    onDragEnd={() => props.doAreaCalc()}
                />
            </React.Fragment>
        );
    });
}
